import { Box, Flex, Text, useColorModeValue, chakra } from '@chakra-ui/react';
import React from 'react';

import type { Route } from 'nextjs-routes';

import Skeleton from 'ui/shared/chakra/Skeleton';
import Hint from 'ui/shared/Hint';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import NextLink from 'ui/shared/NextLink';
import TruncatedValue from 'ui/shared/TruncatedValue';

export type Props = {
  className?: string;
  label: string;
  value: string | React.ReactNode;
  valuePrefix?: string;
  valuePostfix?: string;
  hint?: string | React.ReactNode;
  isLoading?: boolean;
  diff?: string | number;
  diffFormatted?: string;
  diffPeriod?: '24h';
  period?: '1h' | '24h';
  href?: Route;
  icon?: IconName;
  color?: string;
  gap?: string;
  topHint?: boolean;
};

const Container = ({ href, children }: { href?: Route; children: React.JSX.Element }) => {
  if (href) {
    return (
      <NextLink href={ href } passHref legacyBehavior>
        { children }
      </NextLink>
    );
  }

  return children;
};

const StatsWidget = ({
  className,
  icon,
  label,
  value,
  valuePrefix,
  valuePostfix,
  isLoading,
  hint,
  diff,
  diffPeriod = '24h',
  diffFormatted,
  period,
  href,
  color,
  gap,
  topHint,
}: Props) => {
  const bgColor = useColorModeValue('white', '#0A0A0A');
  const skeletonBgColor = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  const hintColor = useColorModeValue('gray.600', 'gray.400');
  const textColor = useColorModeValue(
    'black',
    'white',
  );
  const borderColor = useColorModeValue(
    'rgba(19,19,19,0.20)',
    'rgba(255,255,255,0.20)',
  );

  const getTopHintComponent = () => {
    if (!topHint) return null;

    if (typeof hint === 'string') {
      return (
        <Skeleton isLoaded={ !isLoading } alignSelf="center" borderRadius="base">
          <Hint label={ hint } boxSize={ 6 } color={ hintColor }/>
        </Skeleton>
      );
    }

    return hint;
  };

  const getHintComponent = () => {
    if (topHint) return null;

    if (typeof hint === 'string') {
      return (
        <Skeleton isLoaded={ !isLoading } alignSelf="center" borderRadius="base">
          <Hint label={ hint } boxSize={ 6 } color={ hintColor }/>
        </Skeleton>
      );
    }

    return hint;
  };

  return (
    <Container href={ !isLoading ? href : undefined }>
      <Flex
        className={ className }
        alignItems="center"
        direction="column"
        bgColor={ isLoading ? skeletonBgColor : bgColor }
        p={ 3 }
        borderRadius="base"
        justifyContent="space-between"
        gap={ gap }
        border={ `0.5px solid ${ borderColor }` }
        columnGap={ 2 }
        { ...(href && !isLoading ? {
          as: 'a',
          href,
        } : {}) }
      >
        <Box display="flex" alignItems="center" gap={ topHint ? 1 : 0 } justifyContent={ topHint ? undefined : 'space-between' } w={{ base: '100%', lg: '100%' }}>
          <Skeleton
            isLoaded={ !isLoading }
            color={ topHint ? '#696969' : textColor }
            fontSize="sm"
            lineHeight="14px"
            w="fit-content"
          >
            <h2>{ label }</h2>
          </Skeleton>
          { icon && (
            <IconSvg
              name={ icon }
              p={ 2 }
              boxSize="40px"
              isLoading={ isLoading }
              borderRadius="base"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={ 0 }
              color={ color }
            />
          ) }
          { getTopHintComponent() }
        </Box>
        <Box display="flex" alignItems="center" gap="4px" w={{ base: '100%', lg: '100%' }}>
          <Skeleton
            isLoaded={ !isLoading }
            display="flex"
            alignItems="baseline"
            fontWeight={ 500 }
            fontSize="lg"
            lineHeight={ 6 }
            color={ textColor }
            maxWidth="80%"
          >
            { valuePrefix && <chakra.span whiteSpace="pre">{ valuePrefix }</chakra.span> }
            { typeof value === 'string' ? (
              <TruncatedValue isLoading={ isLoading } value={ value }/>
            ) : (
              value
            ) }
            { valuePostfix && <chakra.span whiteSpace="pre">{ valuePostfix }</chakra.span> }
            { diff && Number(diff) > 0 && (
              <>
                <Text ml={ 2 } mr={ 1 } color={ textColor } overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                  +{ diffFormatted || Number(diff).toLocaleString() }
                </Text>
                <Text variant="secondary" fontSize="sm">({ diffPeriod })</Text>
              </>
            ) }
            { period && <Text variant="secondary" fontSize="xs" fontWeight={ 400 } ml={ 1 }>({ period })</Text> }
          </Skeleton>
          { getHintComponent() }
        </Box>
      </Flex>
    </Container>
  );
};

export default chakra(StatsWidget);
